import { Fragment, useMemo, useState } from "react";
import { Alert, Button, FormGroup, Input, Label, Progress, Table } from "reactstrap";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import PageContainer from "@components/layout/PageContainer";
import LoadingScreen from "@components/content/LoadingScreen";
import { showAlert } from "@utils/Alert";
import { useAppSelector } from "@/store/hooks";
import { formatCant } from "@utils/Cant";
import { SelectOptionType } from "@utils/Utils";
import { commitEDocumentImport } from "@utils/edocumentImport/core/commitEDocumentImport";
import { loadEDocumentImportContext } from "@utils/edocumentImport/core/loadEDocumentImportContext";
import {
    EDocumentFileError,
    ReadEDocumentFilesResult,
    readEDocumentFiles,
} from "@utils/edocumentImport/core/readEDocumentFiles";
import { collectPendingClients, resolveEDocuments } from "@utils/edocumentImport/core/resolveEDocuments";
import {
    EDocumentImportContext,
    EDocumentImportDefinition,
    ResolvedEDocument,
} from "@utils/edocumentImport/core/types";
import { ImportProgress, ImportSummary } from "@utils/import/core/types";
import styles from "./style.module.scss";

type Step = "file" | "review" | "confirm" | "done";

const STATUS_LABELS = {
    new: { text: "Nuevo", color: "text-success" },
    duplicate: { text: "Duplicado", color: "text-secondary" },
    reprocess: { text: "Se reescribe", color: "text-warning" },
    warning: { text: "Advertencia", color: "text-warning" },
    error: { text: "Error", color: "text-danger" },
} as const;

type EDocumentImportWizardProps = {
    definition: EDocumentImportDefinition;
};

/**
 * Asistente de carga masiva de comprobantes electronicos desde XML o ZIP.
 *
 * Los cuatro pasos (archivo, revision, confirmacion, resultado) son siempre los mismos: lo que
 * cambia entre facturas y lo que venga despues (notas de credito, notas de debito) vive en la
 * `EDocumentImportDefinition` que se recibe por props.
 */
const EDocumentImportWizard = ({ definition }: EDocumentImportWizardProps) => {
    const navigate = useNavigate();
    const suscription = useAppSelector((state) => state.suscription.suscription);

    const [step, setStep] = useState<Step>("file");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const [fileName, setFileName] = useState("");
    const [readResult, setReadResult] = useState<ReadEDocumentFilesResult | null>(null);
    const [reprocessExisting, setReprocessExisting] = useState(false);

    const [context, setContext] = useState<EDocumentImportContext | null>(null);
    const [documents, setDocuments] = useState<ResolvedEDocument[]>([]);
    const [expandedFile, setExpandedFile] = useState<string>("");

    const [isImporting, setIsImporting] = useState(false);
    const [progress, setProgress] = useState<ImportProgress>({ processed: 0, total: 0 });
    const [summary, setSummary] = useState<ImportSummary | null>(null);
    const [skippedCount, setSkippedCount] = useState(0);
    const [createdClientsCount, setCreatedClientsCount] = useState(0);

    const productOptions: SelectOptionType[] = useMemo(
        () =>
            (context?.products || [])
                .filter((product) => !!product.id)
                .map((product) => ({
                    value: `${product.id}`,
                    label: `${product.codigo || ""} - ${product.descripcion_fact || ""}`,
                })),
        [context]
    );

    const counts = useMemo(() => {
        const importable = documents.filter((document) => document.action === "create");
        return {
            importable: importable.length,
            duplicates: documents.filter((document) => document.status === "duplicate").length,
            reprocess: documents.filter((document) => document.status === "reprocess").length,
            errors: documents.filter((document) => document.status === "error").length,
            // Se cuentan aparte de los demas errores porque tienen un arreglo concreto que el
            // usuario puede ejecutar: cargar primero el documento referenciado.
            missingReference: documents.filter((document) => document.missingReference).length,
            temporalLines: importable.reduce(
                (total, document) => total + document.lines.filter((line) => line.temporal).length,
                0
            ),
            newClients: collectPendingClients(documents).length,
        };
    }, [documents]);

    const onFileSelected = async (file?: File) => {
        if (!file) return;

        setError("");
        setIsLoading(true);
        try {
            const result = await readEDocumentFiles(file, definition.acceptedRootNames);
            setReadResult(result);
            setFileName(file.name);
        } catch (readError) {
            const message =
                readError instanceof EDocumentFileError
                    ? readError.message
                    : "No fue posible leer el archivo seleccionado.";
            setError(message);
            setReadResult(null);
        } finally {
            setIsLoading(false);
        }
    };

    const goToReview = async () => {
        if (!readResult?.files.length) return;

        setIsLoading(true);
        setError("");
        try {
            const loaded = await loadEDocumentImportContext(
                definition,
                `${suscription?.identificacion || ""}`
            );
            setContext(loaded);
            setDocuments(
                resolveEDocuments({
                    files: readResult.files,
                    context: loaded,
                    definition,
                    reprocessExisting,
                })
            );
            setStep("review");
        } catch (loadError) {
            setError(`No fue posible cargar los datos del sistema: ${(loadError as Error).message}`);
        } finally {
            setIsLoading(false);
        }
    };

    /** Reasigna a mano la linea de un comprobante a un producto del inventario. */
    const changeLineProduct = (fileName: string, lineIndex: number, productId: string) => {
        setDocuments((current) =>
            current.map((document) => {
                if (document.fileName !== fileName) return document;

                const lines = document.lines.map((line, index) =>
                    index === lineIndex ? { ...line, productId, temporal: !productId } : line
                );
                return { ...document, lines };
            })
        );
    };

    const runImport = async () => {
        if (!context) return;

        // A proposito no se usa `isLoading`: el LoadingScreen es un bloqueo a pantalla
        // completa y taparia la barra de progreso.
        setIsImporting(true);
        setError("");
        try {
            const result = await commitEDocumentImport({
                documents,
                context,
                definition,
                onProgress: setProgress,
            });

            // Se guardan los documentos con los clientes ya ligados: si mas adelante se
            // reintenta desde esta misma pantalla, no se vuelven a crear.
            setDocuments(result.documents);
            setSummary(result.summary);
            setSkippedCount(result.skippedCount);
            setCreatedClientsCount((current) => current + result.createdClientsCount);
            setStep("done");
            showAlert(
                "success",
                `Se cargaron ${result.summary.createdCount} ${definition.ui.entityPlural}`
            );
        } catch (importError) {
            setError(`La carga fallo: ${(importError as Error).message}`);
            showAlert("danger", "No fue posible completar la carga");
        } finally {
            setIsImporting(false);
        }
    };

    const renderSteps = () => {
        const stepIndex = { file: 1, review: 2, confirm: 3, done: 4 }[step];

        return (
            <div className={styles.steps}>
                {[
                    { number: 1, label: "Archivo" },
                    { number: 2, label: "Revision" },
                    { number: 3, label: "Confirmacion" },
                    { number: 4, label: "Resultado" },
                ].map((item) => (
                    <div
                        key={item.number}
                        className={`${styles.stepDot} ${stepIndex >= item.number ? styles.stepDotActive : ""}`}
                    >
                        <span>{item.number}</span>
                        <small>{item.label}</small>
                    </div>
                ))}
            </div>
        );
    };

    const renderFileStep = () => (
        <div className={styles.section}>
            <p className={styles.help}>{definition.ui.fileHelpText}</p>

            <Input
                type="file"
                accept=".xml,.zip"
                data-testid="edocument-import-file"
                onChange={(event) => onFileSelected(event.target.files?.[0])}
            />

            <FormGroup check className={styles.reprocess}>
                <Input
                    type="checkbox"
                    id="edocument-import-reprocess"
                    data-testid="edocument-import-reprocess"
                    checked={reprocessExisting}
                    onChange={(event) => setReprocessExisting(event.target.checked)}
                />
                <Label check for="edocument-import-reprocess">
                    Reprocesar los comprobantes que ya estan cargados
                </Label>
                <p className={styles.help}>
                    Reescribe el documento completo con los datos del XML, en vez de omitirlo.
                    Se pierde todo lo que haya cambiado despues de la carga original (pagos
                    aplicados, anulaciones, notas ligadas). Usarlo solo para reparar una carga
                    anterior.
                </p>
            </FormGroup>

            {readResult && (
                <>
                    <p className={styles.help} data-testid="edocument-import-file-summary">
                        {fileName}: {readResult.files.length} comprobantes para cargar,{" "}
                        {readResult.discarded.length} archivos descartados.
                    </p>

                    {readResult.truncatedCount > 0 && (
                        <Alert color="warning" data-testid="edocument-import-truncated">
                            El archivo trae mas comprobantes de los que se pueden cargar de una vez:{" "}
                            {readResult.truncatedCount} quedaron fuera. Divida el ZIP y repita la carga.
                        </Alert>
                    )}

                    {readResult.discarded.length > 0 && (
                        <div className={styles.tableWrapper}>
                            <Table bordered size="sm" responsive>
                                <thead>
                                    <tr>
                                        <th>Archivo descartado</th>
                                        <th>Motivo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {readResult.discarded.map((discarded, index) => (
                                        <tr key={`${discarded.fileName}-${index}`}>
                                            <td>{discarded.fileName}</td>
                                            <td className={styles.messages}>{discarded.reason}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    const renderLines = (document: ResolvedEDocument) => (
        <tr className={styles.linesRow} key={`${document.fileName}-lines`}>
            <td colSpan={definition.previewColumns.length + 4}>
                <Table bordered size="sm" responsive>
                    <thead>
                        <tr>
                            <th>Codigo</th>
                            <th>Detalle</th>
                            <th>Cantidad</th>
                            <th>Precio</th>
                            <th>Producto del sistema</th>
                        </tr>
                    </thead>
                    <tbody>
                        {document.lines.map((line, index) => (
                            <tr key={`${document.fileName}-line-${index}`}>
                                <td>{line.data.codigo}</td>
                                <td>{line.data.description}</td>
                                <td>{formatCant(Number(line.data.cant || 0))}</td>
                                <td>{line.data.price}</td>
                                <td className={styles.productPicker}>
                                    <Select
                                        isClearable
                                        placeholder={
                                            line.temporal ? "Producto temporal" : "Buscar producto"
                                        }
                                        className={line.temporal ? styles.temporal : styles.linked}
                                        options={productOptions}
                                        value={productOptions.find(
                                            (option) => option.value === line.productId
                                        )}
                                        onChange={(option) =>
                                            changeLineProduct(
                                                document.fileName,
                                                index,
                                                `${(option as SelectOptionType)?.value || ""}`
                                            )
                                        }
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </td>
        </tr>
    );

    const renderReviewStep = () => (
        <div className={styles.section}>
            <p className={styles.help}>{definition.ui.reviewHelpText}</p>

            <div className={styles.counters} data-testid="edocument-import-counters">
                <div>
                    <strong>{counts.importable}</strong>
                    <small>Se van a cargar</small>
                </div>
                <div>
                    <strong>{counts.duplicates}</strong>
                    <small>Duplicados</small>
                </div>
                {!!counts.reprocess && (
                    <div data-testid="edocument-import-reprocess-count">
                        <strong>{counts.reprocess}</strong>
                        <small>Se reescriben</small>
                    </div>
                )}
                <div>
                    <strong>{counts.errors}</strong>
                    <small>Con error</small>
                </div>
                {!!counts.missingReference && (
                    <div data-testid="edocument-import-missing-reference">
                        <strong>{counts.missingReference}</strong>
                        <small>Sin factura en el sistema</small>
                    </div>
                )}
                {!definition.requiresReference && (
                    <div>
                        <strong>{counts.newClients}</strong>
                        <small>Clientes por crear</small>
                    </div>
                )}
                <div>
                    <strong>{counts.temporalLines}</strong>
                    <small>Lineas temporales</small>
                </div>
            </div>

            <div className={styles.tableWrapper}>
                <Table bordered size="sm" responsive>
                    <thead>
                        <tr>
                            <th />
                            <th>Estado</th>
                            {definition.previewColumns.map((column) => (
                                <th key={column.header}>{column.header}</th>
                            ))}
                            <th>Cliente</th>
                            <th>Observaciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {documents.map((document) => (
                            <Fragment key={document.fileName}>
                                <tr>
                                    <td
                                        className={styles.expandCell}
                                        data-testid={`edocument-expand-${document.parsed.numeroConsecutivo}`}
                                        onClick={() =>
                                            setExpandedFile(
                                                expandedFile === document.fileName ? "" : document.fileName
                                            )
                                        }
                                    >
                                        {expandedFile === document.fileName ? "-" : "+"}
                                    </td>
                                    <td className={STATUS_LABELS[document.status].color}>
                                        {STATUS_LABELS[document.status].text}
                                    </td>
                                    {definition.previewColumns.map((column) => (
                                        <td key={column.header}>{column.render(document)}</td>
                                    ))}
                                    <td>
                                        {document.clientId
                                            ? "Ligado"
                                            : document.pendingClient
                                              ? "Se va a crear"
                                              : "Sin cliente"}
                                    </td>
                                    <td className={styles.messages}>{document.messages.join(". ")}</td>
                                </tr>
                                {expandedFile === document.fileName && renderLines(document)}
                            </Fragment>
                        ))}
                    </tbody>
                </Table>
            </div>
        </div>
    );

    const renderConfirmStep = () => (
        <div className={styles.section}>
            <p className={styles.help}>{definition.ui.confirmHelpText}</p>

            <Alert color="info" data-testid="edocument-import-confirm">
                Se van a cargar {counts.importable} {definition.ui.entityPlural}, creando{" "}
                {counts.newClients} clientes nuevos. {counts.temporalLines} lineas quedaran como
                producto temporal. Se omitiran {counts.duplicates} duplicados y {counts.errors} con
                error.
                {!!counts.reprocess && (
                    <span data-testid="edocument-import-confirm-reprocess">
                        {" "}
                        De los que se van a cargar, {counts.reprocess} ya existian y se
                        reescribiran con los datos del XML.
                    </span>
                )}
            </Alert>

            {progress.total > 0 && (
                <Progress
                    value={progress.processed}
                    max={progress.total}
                    data-testid="edocument-import-progress"
                >
                    {progress.processed} / {progress.total}
                </Progress>
            )}
        </div>
    );

    const renderDoneStep = () => (
        <div className={styles.section} data-testid="edocument-import-result">
            <Alert color="success">
                Se cargaron {summary?.createdCount || 0} {definition.ui.entityPlural} y se crearon{" "}
                {createdClientsCount} clientes. Se omitieron {skippedCount}.
            </Alert>

            {!!summary?.failures.length && (
                <Alert color="danger">
                    {summary.failures.length} comprobantes no se pudieron cargar:
                    <ul>
                        {summary.failures.slice(0, 10).map((failure) => (
                            <li key={failure.index}>
                                Documento {failure.index + 1}: {failure.message}
                            </li>
                        ))}
                    </ul>
                </Alert>
            )}

            <p className={styles.help}>{definition.ui.doneHelpText}</p>

            <Button color="primary" onClick={() => navigate(definition.ui.backUrl)}>
                {definition.ui.doneActionTitle}
            </Button>
        </div>
    );

    const primaryActions = () => {
        if (step === "file") {
            return [
                {
                    actionTitle: "Continuar",
                    color: "success",
                    disabled: !readResult?.files.length,
                    onClick: goToReview,
                },
            ];
        }
        if (step === "review") {
            return [
                { actionTitle: "Volver", color: "secondary", onClick: () => setStep("file") },
                {
                    actionTitle: "Continuar",
                    color: "success",
                    disabled: counts.importable === 0,
                    onClick: () => setStep("confirm"),
                },
            ];
        }
        if (step === "confirm") {
            return [
                {
                    actionTitle: "Volver",
                    color: "secondary",
                    disabled: isImporting,
                    onClick: () => setStep("review"),
                },
                {
                    actionTitle: isImporting ? "Cargando..." : "Cargar",
                    color: "success",
                    // Deshabilitado mientras corre para que un doble clic no cargue dos veces.
                    disabled: isImporting || counts.importable === 0,
                    onClick: runImport,
                },
            ];
        }
        return [];
    };

    return (
        <PageContainer
            title={definition.ui.title}
            backUrl={definition.ui.backUrl}
            primaryActions={primaryActions()}
        >
            {isLoading && <LoadingScreen />}

            {renderSteps()}

            {error && (
                <Alert color="danger" data-testid="edocument-import-error">
                    {error}
                </Alert>
            )}

            {step === "file" && renderFileStep()}
            {step === "review" && renderReviewStep()}
            {step === "confirm" && renderConfirmStep()}
            {step === "done" && renderDoneStep()}
        </PageContainer>
    );
};

export default EDocumentImportWizard;
